import React, { useEffect, useState } from 'react';
import { Loader } from 'js/components/common/Loader';
import { useIsMounted } from 'js/hooks/useIsMounted';

import * as constants from './constants';
import * as Types from './types';

import style from './UnicoMultiFactorAuthentication.scss';

export const UnicoMultiFactorAuthentication = ({
    hostKeys,
    environment,
    setResponse,
    setError
}: Types.UnicoMultiFactorAuthenticationProps) => {
    const isMounted = useIsMounted();
    const [unico, setUnico] = useState<Types.UnicoSdkInstance | null>(null);
    const [cameraOpener, setCameraOpener] = useState<Types.UnicoCameraOpener | null>(null);

    useEffect(() => {
        (async () => {
            if (!isMounted()) {
                return;

            }

            if (window[constants.UNICO_SDK_PROMISE]) {
                await window[constants.UNICO_SDK_PROMISE];
            } else {
                const sdkUrl = window[constants.UNICO_SDK_KEY];

                if (!sdkUrl) {
                    setError(constants.NO_SDK_ERROR);
                    return;
                }

                const promise = import(/* webpackIgnore: true */ sdkUrl);

                window[constants.UNICO_SDK_PROMISE] = promise;

                await promise;
            }

            setUnico(await window[constants.UNICO_SDK_PROMISE]);
        })();
    }, [
        setError,
        isMounted
    ]);

    useEffect(() => {
        if (!unico || !isMounted()) {
            return;
        }

        (async () => {
            let hostKey: string;

            try {
                const hostKeysMap = JSON.parse(hostKeys);

                hostKey = hostKeysMap[window.location.origin];

                if (!hostKey) {
                    setError(constants.MISSING_HOSTNAME);
                    return;
                }
            } catch (error) {
                setError(constants.INCORRECT_CONFIGURATION);
                return;
            }

            const config = new unico.UnicoConfig()
                .setHostname(window.location.origin)
                .setHostKey(hostKey);

            const unicoCameraBuilder = new unico.UnicoCheckBuilder();

            switch (environment) {
                case 'UAT':
                    unicoCameraBuilder.setEnvironment(unico.SDKEnvironmentTypes.UAT);
                    break;

                default:
                    unicoCameraBuilder.setEnvironment(unico.SDKEnvironmentTypes.PROD);
            }

            unicoCameraBuilder.setTheme(new unico.UnicoThemeBuilder().build());
            unicoCameraBuilder.setLocale(unico.LocaleTypes.EN_US);

            const unicoCamera = unicoCameraBuilder.build();
            const cameraOpenerInstance = await unicoCamera.prepareSelfieCamera(config, unico.SelfieCameraTypes.NORMAL);

            // Unico has cameraOpenerInstance extending Function, so we have to use function
            // to assign it to the state
            setCameraOpener(() => cameraOpenerInstance);
        })();
    }, [
        environment,
        setError,
        isMounted,
        unico,
        hostKeys
    ]);

    useEffect(() => {
        (async () => {
            if (!cameraOpener || !isMounted()) {
                return;
            }

            try {
                // ensure there are no focused input elements before invoking the SDK
                if (document.activeElement instanceof HTMLElement) {
                    document.activeElement?.blur();
                }

                cameraOpener.open({
                    on: {
                        success: ({ encrypted }) => setResponse({ encrypted }),
                        error: (error) => setError(error)
                    }
                });
            } catch (exception) {
                setError(exception);
            }
        })();
    }, [
        isMounted,
        cameraOpener,
        setError,
        setResponse
    ]);

    return (
        <div className={style.container}>
            <Loader />
        </div>
    );
};
